// src/pages/dashboard/branch/menu-items/MenuItemDetailPage.tsx
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	FiArrowLeft,
	FiAward,
	FiBarChart,
	FiChevronRight,
	FiDollarSign,
	FiEdit2,
	FiEye,
	FiEyeOff,
	FiGlobe,
	FiHeart,
	FiInfo,
	FiStar,
	FiTrash2,
	FiZap
} from 'react-icons/fi';
import { Link, useNavigate, useParams } from 'react-router-dom';

import Badge from '../../../../../components/ui/Badge';
import Button from '../../../../../components/ui/Button';
import Card from '../../../../../components/ui/Card';
import Skeleton from '../../../../../components/ui/Skeleton';
import { getItemDescription, getItemIngredients, getItemName, hasArabicTranslation } from '../../../../../utils/menuItemHelper';
import { MenuItem } from '../../../../../utils/supabaseClient';
import DeleteMenuItemModal from '../components/DeleteMenuItemModal';
import UpdateMenuItemModal from '../components/update-item-modal/updateMenuItemModal';

import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import { selectCategories, selectCategoryLoading, selectCurrentBranch, selectMenuItemLoading, selectMenuItemLoadingAnalytics, selectMenuItems, selectMenuItemsAnalytics } from '../../../../../store/selectors';
import { fetchCategories } from '../../../../../store/slices/categorySlice';
import { fetchMenuItemAnalytics, fetchMenuItems, updateMenuItemData } from '../../../../../store/slices/menuItemSlice';
import { FOOD_TAGS } from '../../../../../utils/foodTags';
import HeroSection from './components/HeroSection';
import ItemStatistics from './components/ItemStatistics';
import MobileActionsTab from './components/MobileActionsTab';
import MobileAnalyticsTab from './components/MobileAnalyticsTab';
import DietaryPreferencesSection from './components/DietaryPreferencesSection';
import IngredientsInfoSection from './components/IngredientsInfoSection';

const MenuItemDetailPage: React.FC = () => {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const isRTL = i18n.language === 'ar';

	const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [displayLanguage, setDisplayLanguage] = useState<'en' | 'ar'>(isRTL ? 'ar' : 'en');
	const [activeTab, setActiveTab] = useState<'overview' | 'analytics' | 'actions'>('overview');

	const { branchId, itemId } = useParams<{ branchId: string; itemId: string }>();

	const dispatch = useAppDispatch()
	const isLoading = useAppSelector(selectMenuItemLoading)
	const menuItems = useAppSelector(selectMenuItems)
	const menuItem = menuItems.find(item => item.id === itemId)

	const categories = useAppSelector(selectCategories)
	const isLoadingCategories = useAppSelector(selectCategoryLoading)

	const isLoadingAnalytics = useAppSelector(selectMenuItemLoadingAnalytics)
	const analytics = useAppSelector(selectMenuItemsAnalytics);
	const itemAnalytics = analytics[itemId!] ?? []
	const branch = useAppSelector(selectCurrentBranch)
	// const isLoadingBranch = useAppSelector(selectBranchLoading)
	// const organization = useAppSelector(selectCurrentOrganization)

	useEffect(() => {
		if (!isLoading && !menuItem) {
			dispatch(fetchMenuItems({ branchId: branchId! }))
		}
		if (!isLoadingCategories && !categories.length) {
			dispatch(fetchCategories({ branchId: branchId! }))
		}
		if (itemId && !isLoadingAnalytics && !itemAnalytics.length) {
			dispatch(fetchMenuItemAnalytics({ itemId, branchId: branchId! }))
		}

		// if (!isLoadingBranch && !branch && branchId && organization) {
		// 	dispatch(fetchBranchById({branchId, organizationId: organization.id}))
		// }
	}, [itemId, branchId]);

	useEffect(() => {
		// Set default display language based on current app language
		setDisplayLanguage(isRTL ? 'ar' : 'en');
	}, [isRTL]);

	const togglePublishStatus = async () => {
		if (!menuItem) return;

		try {
			const updatedData = {
				id: menuItem.id,
				is_published: !menuItem.is_published
			};

			await dispatch(updateMenuItemData(updatedData))
		} catch (error) {
			console.error('Error updating menu item:', error);
		}
	};

	const toggleAvailabilityStatus = async () => {
		if (!menuItem) return;

		try {
			const updatedData = {
				id: menuItem.id,
				is_available: !menuItem.is_available
			};

			await dispatch(updateMenuItemData(updatedData))
		} catch (error) {
			console.error('Error updating menu item:', error);
		}
	};

	const toggleFeaturedStatus = async () => {
		if (!menuItem) return;

		try {
			const updatedData = {
				id: menuItem.id,
				is_featured: !menuItem.is_featured
			};

			await dispatch(updateMenuItemData(updatedData))
		} catch (error) {
			console.error('Error updating menu item:', error);
		}
	};

	const handleUpdateMenuItem = (updatedItem: MenuItem) => {
		setIsUpdateModalOpen(false);
	};

	const handleDeleteMenuItem = () => {
		navigate(`/dashboard/${branchId}/menu`);
	};

	// Animation variants
	const pageVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.6,
				staggerChildren: 0.1
			}
		}
	};

	const cardVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: { opacity: 1, y: 0 }
	};

	// Calculate itemAnalytics averages
	const calculateAnalyticsAverages = () => {
		if (!itemAnalytics.length) return { avgClicks: 0, avgTimeSpent: 0 };

		const totalClicks = itemAnalytics.reduce((sum, item) => sum + item.click_count, 0);
		const totalTimeSpent = itemAnalytics.reduce((sum, item) => sum + item.time_spent, 0);

		return {
			avgClicks: Math.round(totalClicks / itemAnalytics.length),
			avgTimeSpent: ((totalTimeSpent / itemAnalytics.length) / 60).toFixed(2) // Convert to minutes
		};
	};

	const analyticsAverages = calculateAnalyticsAverages();

	// Check if menu item has translations
	const hasTranslation = menuItem ? hasArabicTranslation(menuItem) : false;

	// Get the appropriate name, description, and ingredients based on display language
	const itemName = menuItem ? getItemName(menuItem, displayLanguage) : '';
	const itemDescription = menuItem ? getItemDescription(menuItem, displayLanguage) : '';
	const itemIngredients = menuItem ? getItemIngredients(menuItem, displayLanguage) : [];

	if (isLoading) {
		return (
			<div className="min-h-screen bg-gray-50 dark:bg-gray-900">
				<div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-4 sm:py-8">
					<div className="flex items-center gap-4 mb-6">
						<Skeleton width="w-8" height="h-8" rounded="rounded-full" />
						<Skeleton width="w-40" height="h-6" />
					</div>

					{/* Hero Section Skeleton */}
					<div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm overflow-hidden mb-6">
						<div className="relative">
							<Skeleton width="w-full" height="h-64 sm:h-80" />
							<div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
							<div className="absolute bottom-6 start-6 end-6">
								<Skeleton width="w-3/4" height="h-6 sm:h-8" className="mb-3" />
								<Skeleton width="w-1/2" height="h-4 sm:h-6" />
							</div>
						</div>
					</div>

					{/* Content Grid Skeleton */}
					<div className="space-y-6">
						{[1, 2, 3].map((i) => (
							<div key={i} className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6">
								<Skeleton width="w-32" height="h-6" className="mb-4" />
								<Skeleton width="w-full" height="h-20" />
							</div>
						))}
					</div>
				</div>
			</div>
		);
	}

	if (!menuItem) {
		return (
			<div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
				<div className="text-center max-w-md mx-auto">
					<div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
						<FiInfo className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400" />
					</div>
					<h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
						{t('menuItems.itemNotFound')}
					</h2>
					<p className="text-gray-500 dark:text-gray-400 mb-6 sm:mb-8 text-sm sm:text-base">
						{t('menuItems.itemNotFoundDescription')}
					</p>
					<Link to={`/dashboard/${branchId}/menu`}>
						<Button leftIcon={isRTL ? <FiChevronRight /> : <FiArrowLeft />} size="lg">
							{t('menuItems.backToMenuItems')}
						</Button>
					</Link>
				</div>
			</div>
		);
	}

	return (
		<motion.div
			variants={pageVariants}
			initial="hidden"
			animate="visible"
			className="min-h-screen bg-gray-50 dark:bg-gray-900"
		>
			<div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-4 sm:py-8">
				{/* Breadcrumb */}
				<motion.div variants={cardVariants} className="mb-4 sm:mb-6">
					<Link
						to={`/dashboard/${branchId}/menu`}
						className="inline-flex items-center text-sm font-medium text-primary hover:text-primary-dark"
					>
						{isRTL ? <FiChevronRight className="ms-2 h-4 w-4" /> : <FiArrowLeft className="me-2 h-4 w-4" />}
						{t('menuItems.backToMenuItems')}
					</Link>
				</motion.div>

				{/* Hero Section */}
				<HeroSection
					cardVariants={cardVariants}
					itemName={itemName}
					hasTranslation={hasTranslation}
					menuItem={menuItem}
					displayLanguage={displayLanguage}
					setDisplayLanguage={setDisplayLanguage}
					setIsDeleteModalOpen={setIsDeleteModalOpen}
					setIsUpdateModalOpen={setIsUpdateModalOpen}
					togglePublishStatus={togglePublishStatus}
				/>

				{/* Mobile Tabs */}
				<div className="sm:hidden mb-6">
					<div className="flex bg-white dark:bg-gray-800 rounded-lg p-1 shadow-sm">
						{[
							{ id: 'overview', label: t('menuItems.overview'), icon: FiInfo },
							{ id: 'analytics', label: t('menuItems.analytics'), icon: FiBarChart },
							{ id: 'actions', label: t('menuItems.actions'), icon: FiZap }
						].map(({ id, label, icon: Icon }) => (
							<button
								key={id}
								onClick={() => setActiveTab(id as any)}
								className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${activeTab === id
									? 'bg-primary text-white'
									: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
									}`}
							>
								<Icon className="w-4 h-4" />
								<span className="hidden xs:inline">{label}</span>
							</button>
						))}
					</div>
				</div>

				{/* Content Grid */}
				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
					<div className='lg:col-span-2 space-y-6'>
						{/* Main Content */}
						<div className={`lg:col-span-2 space-y-6 ${activeTab !== 'overview' ? 'hidden sm:block' : ''}`}>
							{/* Description */}
							{itemDescription && (
								<motion.div variants={cardVariants}>
									<Card className="p-4 sm:p-6 border-none shadow-md">
										<div className="flex items-center gap-3 mb-4">
											<div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
												<FiInfo className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-400" />
											</div>
											<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
												{t('menuItems.description')}
											</h3>

											{hasTranslation && (
												<div className="ms-auto">
													<Badge
														variant="primary"
														className="text-xs flex items-center gap-1"
														icon={<FiGlobe className="w-3 h-3" />}
													>
														{displayLanguage === 'en' ? 'English' : 'العربية'}
													</Badge>
												</div>
											)}
										</div>
										<p
											className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm sm:text-base"
											dir={displayLanguage === 'ar' ? 'rtl' : 'ltr'}
										>
											{itemDescription}
										</p>
									</Card>
								</motion.div>
							)}

							{/* Pricing Details */}
							<motion.div variants={cardVariants}>
								<Card className="p-4 sm:p-6 border-none shadow-md">
									<div className="flex items-center gap-3 mb-4 sm:mb-6">
										<div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
											<FiDollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 dark:text-green-400" />
										</div>
										<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
											{t('menuItems.pricingDetails')}
										</h3>
									</div>

									<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
										<div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
											<label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
												{t('menuItems.regularPrice')}
											</label>
											<div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
												${menuItem.price?.toFixed(2)}
											</div>
										</div>

										{!!menuItem.discounted_price && (
											<div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
												<label className="block text-sm font-medium text-green-600 dark:text-green-400 mb-2">
													{t('menuItems.discountedPrice')}
												</label>
												<div className="text-2xl sm:text-3xl font-bold text-green-700 dark:text-green-400">
													${menuItem.discounted_price.toFixed(2)}
												</div>
												<div className="text-xs sm:text-sm text-green-600 dark:text-green-400 mt-1">
													{t('menuItems.saveAmount', {
														amount: (menuItem.price! - menuItem.discounted_price).toFixed(2),
														percentage: Math.round(((menuItem.price! - menuItem.discounted_price) / menuItem.price!) * 100)
													})}
												</div>
											</div>
										)}
									</div>
								</Card>
							</motion.div>

							{/* Dietary Information */}
							{/* <motion.div variants={cardVariants}>
								<Card className="p-4 sm:p-6 border-none shadow-md">
									<div className="flex items-center gap-3 mb-4 sm:mb-6">
										<div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
											<FiHeart className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" />
										</div>
										<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
											{t('menuItems.dietaryInformation')}
										</h3>
									</div>

									<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
										{
											menuItem?.tags?.map(tagId => {
												const tag = FOOD_TAGS[tagId];
												return <div
													key={tagId}
													className={`flex items-center gap-2 sm:gap-3 p-3 rounded-xl border transition-all ${tag.color}`}
												>
													<span className="text-base sm:text-lg">{tag.icon}</span>
													<div>
														<div className={`text-xs sm:text-sm font-medium`}>
															{i18n.language == 'ar' ? tag.labelAr : tag.labelEn}
														</div>
													</div>
												</div>
											}
											)
										}
									</div>
								</Card>
							</motion.div> */}

							{/* Dietary Preferences Section */}
							<motion.div variants={cardVariants}>
								<DietaryPreferencesSection
									itemId={itemId!}
									cardVariants={cardVariants}
								/>
							</motion.div>

							{/* Ingredients Info Section */}
							<motion.div variants={cardVariants}>
								<IngredientsInfoSection
									itemId={itemId!}
									cardVariants={cardVariants}
								/>
							</motion.div>
						</div>

						{/* Analytics Tab (Mobile) */}
						<div className={`lg:col-span-2 space-y-6 ${activeTab !== 'analytics' ? 'hidden sm:block' : ''}`}>
							<MobileAnalyticsTab
								analyticsAverages={analyticsAverages}
								itemAnalytics={itemAnalytics.map(item => ({ ...item, id: item.item_id }))}
								cardVariants={cardVariants}
							/>
						</div>

						{/* Actions Tab (Mobile) */}
						<div className={`lg:col-span-2 space-y-6 ${activeTab !== 'actions' ? 'hidden' : ''}`}>
							<MobileActionsTab
								menuItem={menuItem}
								cardVariants={cardVariants}
								setIsDeleteModalOpen={setIsDeleteModalOpen}
								setIsUpdateModalOpen={setIsUpdateModalOpen}
								toggleAvailabilityStatus={toggleAvailabilityStatus}
								toggleFeaturedStatus={toggleFeaturedStatus}
								togglePublishStatus={togglePublishStatus}
							/>
						</div>
					</div>
					{/* Sidebar - Hidden on mobile, content moved to tabs */}
					<div className="hidden sm:block space-y-6">
						{/* Quick Actions */}
						{/* <motion.div variants={cardVariants}>
							<Card className="p-6 border-none shadow-md">
								<div className="flex items-center gap-3 mb-6">
									<div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
										<FiZap className="w-5 h-5 text-purple-600 dark:text-purple-400" />
									</div>
									<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
										{t('menuItems.quickActions')}
									</h3>
								</div>

								<div className="space-y-3">
									<Button
										variant="outline"
										fullWidth
										leftIcon={menuItem.is_available ? <FiEyeOff /> : <FiEye />}
										onClick={toggleAvailabilityStatus}
										className={`justify-start ${!menuItem.is_available
											? 'border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
											: 'border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
											}`}
									>
										{menuItem.is_available ? t('menuItems.markUnavailable') : t('menuItems.markAvailable')}
									</Button>

									<Button
										variant="outline"
										fullWidth
										leftIcon={<FiStar />}
										onClick={toggleFeaturedStatus}
										className={`justify-start ${menuItem.is_featured
											? 'border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20'
											: 'hover:border-amber-200 dark:hover:border-amber-800 hover:text-amber-600 dark:hover:text-amber-400'
											}`}
									>
										{menuItem.is_featured ? t('menuItems.unfeature') : t('menuItems.feature')}
									</Button>
								</div>
							</Card>
						</motion.div> */}
						<div className={`lg:col-span-2 space-y-6 ${activeTab !== 'actions' ? 'hidden sm:block' : ''}`}>
							<motion.div variants={cardVariants}>
								<Card className="p-4 sm:p-6 border-none shadow-md">
									<div className="flex items-center gap-3 mb-4 sm:mb-6">
										<div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
											<FiZap className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
										</div>
										<h3 className="text-lg font-semibold text-gray-900 dark:text-white">
											{t('menuItems.quickActions')}
										</h3>
									</div>

									<div className="space-y-3 sm:space-y-4">
										<Button
											variant="outline"
											fullWidth
											leftIcon={menuItem.is_published ? <FiEyeOff /> : <FiEye />}
											onClick={togglePublishStatus}
											className={`justify-start ${!menuItem.is_published
												? 'border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
												: 'border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900/20'
												}`}
										>
											{menuItem.is_published ? t('menuItems.unpublish') : t('menuItems.publish')}
										</Button>

										<Button
											variant="outline"
											fullWidth
											leftIcon={menuItem.is_available ? <FiEyeOff /> : <FiEye />}
											onClick={toggleAvailabilityStatus}
											className={`justify-start ${!menuItem.is_available
												? 'border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'
												: 'border-green-200 dark:border-green-800 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20'
												}`}
										>
											{menuItem.is_available ? t('menuItems.markUnavailable') : t('menuItems.markAvailable')}
										</Button>

										<Button
											variant="outline"
											fullWidth
											leftIcon={<FiStar />}
											onClick={toggleFeaturedStatus}
											className={`justify-start ${menuItem.is_featured
												? 'border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20'
												: 'hover:border-amber-200 dark:hover:border-amber-800 hover:text-amber-600 dark:hover:text-amber-400'
												}`}
										>
											{menuItem.is_featured ? t('menuItems.unfeature') : t('menuItems.feature')}
										</Button>

										<div className="pt-2 border-t border-gray-200 dark:border-gray-700">
											<Button
												fullWidth
												leftIcon={<FiEdit2 />}
												onClick={() => setIsUpdateModalOpen(true)}
												className="mb-3"
											>
												{t('common.edit')} {t('menuItems.item')}
											</Button>

											<Button
												variant="outline"
												fullWidth
												leftIcon={<FiTrash2 />}
												onClick={() => setIsDeleteModalOpen(true)}
												className="border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
											>
												{t('common.delete')} {t('menuItems.item')}
											</Button>
										</div>
									</div>
								</Card>
							</motion.div>
						</div>

						{/* Item Statistics */}
						<ItemStatistics
							cardVariants={cardVariants}
							menuItem={menuItem}
							analyticsAverages={analyticsAverages}
							itemAnalytics={itemAnalytics.map(item => ({ ...item, id: item.item_id }))}
						/>

						{/* Performance Badge */}
						<motion.div variants={cardVariants}>
							<Card className="p-6 border-none shadow-md bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
								<div className="text-center">
									<div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-4">
										<FiAward className="w-8 h-8 text-white" />
									</div>
									<h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
										{t('menuItems.itemPerformance')}
									</h4>
									<p className="text-sm text-gray-600 dark:text-gray-400">
										{t('menuItems.performanceDescription')}
									</p>
								</div>
							</Card>
						</motion.div>
					</div>
				</div>
			</div>

			{/* Modals */}
			{menuItem && (
				<>
					<UpdateMenuItemModal
						isOpen={isUpdateModalOpen}
						onClose={() => setIsUpdateModalOpen(false)}
						onMenuItemUpdated={handleUpdateMenuItem}
						menuItem={menuItem}
						categories={categories}
						branchId={branchId!}
					/>

					<DeleteMenuItemModal
						isOpen={isDeleteModalOpen}
						onClose={() => setIsDeleteModalOpen(false)}
						onMenuItemDeleted={handleDeleteMenuItem}
						menuItem={menuItem}
					/>
				</>
			)}
		</motion.div>
	);
};

export default MenuItemDetailPage;